import { customFolders as text, documentTypes } from '../constants/appConstants';

// ─────────────────────────────────────────────────────────────────────────────
// Custom folders: company-configured tabs on the documents page.
//
// The backend (tierb, api/utilities/customFolders.ts) owns the rule — which
// document shows in which folder — and returns each folder as one more bucket,
// keyed `folder:<id>`, in the same list response the fixed tabs come from. This
// module is the frontend half: the tab entry for each folder, the request key
// for it, and the settings-screen validation, so the tab row, the table, the
// counts and the canvas all agree on what a folder tab is called.
//
// A folder TAB is identified by its bucket key, never by its display name: a
// folder named "Approved" must not collide with the Approved tab, and a rename
// must not lose the selection. The name is only what the tab shows.
// ─────────────────────────────────────────────────────────────────────────────

export const FOLDER_TAB_PREFIX = 'folder:';
export const MAX_CUSTOM_FOLDERS = 5;
export const FOLDER_NAME_MAX_LENGTH = 40;
export const FOLDER_MODES = { AUTO: 'auto', MANUAL: 'manual' };

// Built-in tab names a folder may not borrow (case-insensitive).
export const RESERVED_FOLDER_NAMES = [
  'To Review',
  'In Q&A',
  'In Review',
  'On Hold',
  'Classify/Split',
  'Rejected',
  'Approved',
  'Exported',
  'Company Documents'
];

// The stages an auto rule can be narrowed to, as the user sees the tab row.
// `statuses` are the RESOLVED list statuses the backend matches against — the
// same vocabulary the tab buckets use — so "To Review" here is exactly the set
// that tab shows.
export const FOLDER_STAGES = [
  { key: 'toReview', label: 'To Review', statuses: ['Populated', 'Pending', 'Duplicate', 'Error'] },
  { key: 'inReview', label: 'In Review', statuses: ['Waiting For Approval'] },
  { key: 'onHold', label: 'On Hold', statuses: ['on Hold'] },
  { key: 'rejected', label: 'Rejected', statuses: ['Reject'] },
  { key: 'approved', label: 'Approved', statuses: ['Approved'] }
];

// Document types an auto folder can collect. Exported is deliberately not a
// stage: a document the ERP has taken stays in Exported whatever its type.
export const FOLDER_DOCUMENT_TYPES = [
  { value: documentTypes.DELIVERY_TICKET, label: 'Delivery Ticket' },
  { value: documentTypes.INVOICE, label: 'Invoice' },
  { value: documentTypes.RECEIPT, label: 'Receipt' },
  { value: documentTypes.G702, label: 'G702 / Pay App' },
  { value: documentTypes.ESTIMATE, label: 'Estimate' },
  { value: documentTypes.PURCHASE_ORDER, label: 'Purchase Order' }
];

// The two templates the settings screen offers, after the three customer asks
// that produced this feature: tickets out of the queue they clog, retention
// invoices parked where they can be found again.
export const FOLDER_TEMPLATES = [
  {
    key: 'deliveryTickets',
    label: text.TEMPLATE_DELIVERY_TICKETS,
    folder: {
      name: 'Delivery Tickets',
      mode: FOLDER_MODES.AUTO,
      enabled: true,
      rule: { documentType: documentTypes.DELIVERY_TICKET, statuses: [] }
    }
  },
  {
    key: 'retentionInvoices',
    label: text.TEMPLATE_RETENTION_INVOICES,
    folder: { name: 'Retention Invoices', mode: FOLDER_MODES.MANUAL, enabled: true }
  }
];

export const isFolderTab = (tab) => typeof tab === 'string' && tab.startsWith(FOLDER_TAB_PREFIX);

export const folderTabKey = (folder) => `${FOLDER_TAB_PREFIX}${folder?.id}`;

export const folderIdFromTab = (tab) =>
  isFolderTab(tab) ? tab.slice(FOLDER_TAB_PREFIX.length) : null;

/**
 * The company's folders as stored, in the shape the screen edits. Mongo hands
 * back subdocument `_id`s on saved rows — dropped here so they never ride back
 * out in the next settings payload, which rejects unknown keys.
 */
export const normalizeCustomFolders = (saved) =>
  (Array.isArray(saved) ? saved : [])
    .filter((folder) => folder && typeof folder === 'object')
    .map((folder, index) => {
      const mode = folder?.mode === FOLDER_MODES.AUTO ? FOLDER_MODES.AUTO : FOLDER_MODES.MANUAL;
      const normalized = {
        id: folder?.id ? String(folder.id) : '',
        name: String(folder?.name ?? ''),
        order: index,
        enabled: folder?.enabled !== false,
        mode
      };
      if (mode === FOLDER_MODES.AUTO) {
        normalized.rule = {
          documentType: String(folder?.rule?.documentType ?? ''),
          statuses: Array.isArray(folder?.rule?.statuses)
            ? folder.rule.statuses.filter(Boolean)
            : []
        };
      }
      return normalized;
    });

export const getCustomFolders = (company) => normalizeCustomFolders(company?.customFolders);

export const getEnabledFolders = (company) => getCustomFolders(company).filter((f) => f.enabled);

export const getManualFolders = (company) =>
  getEnabledFolders(company).filter((f) => f.mode === FOLDER_MODES.MANUAL);

export const findFolderById = (company, folderId) =>
  folderId ? getCustomFolders(company).find((f) => f.id === String(folderId)) || null : null;

/** Tab entries for the enabled folders, in the order the settings screen set. */
export const folderTabsFor = (company) =>
  getEnabledFolders(company).map((folder) => ({
    name: folderTabKey(folder),
    label: folder.name,
    folderId: folder.id
  }));

/** The display name for a folder tab key, or the tab itself for a fixed tab. */
export const folderTabLabel = (tab, company) => {
  if (!isFolderTab(tab)) return tab;
  return findFolderById(company, folderIdFromTab(tab))?.name || text.UNKNOWN_FOLDER;
};

/**
 * Why a folder row cannot be saved, or '' when it can. Mirrors what the backend
 * would drop silently (see normalizeCustomFolders there) plus the two things
 * only the screen can judge: a name that shadows a built-in tab, and a name
 * already used by another folder.
 */
export const getFolderError = (folder, allFolders = []) => {
  const name = String(folder?.name ?? '').trim();
  if (!name) return text.ERROR_NAME_REQUIRED;
  if (name.length > FOLDER_NAME_MAX_LENGTH) return text.ERROR_NAME_TOO_LONG;
  const lower = name.toLowerCase();
  if (RESERVED_FOLDER_NAMES.some((reserved) => reserved.toLowerCase() === lower)) {
    return text.ERROR_NAME_RESERVED;
  }
  const duplicate = allFolders.some(
    (other) =>
      other !== folder &&
      String(other?.name ?? '')
        .trim()
        .toLowerCase() === lower
  );
  if (duplicate) return text.ERROR_NAME_DUPLICATE;
  if (folder?.mode === FOLDER_MODES.AUTO && !folder?.rule?.documentType) {
    return text.ERROR_TYPE_REQUIRED;
  }
  return '';
};

export const getFoldersError = (folders = []) => {
  if (folders.length > MAX_CUSTOM_FOLDERS) return text.ERROR_TOO_MANY;
  for (const folder of folders) {
    const error = getFolderError(folder, folders);
    if (error) return error;
  }
  return '';
};
